import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { VersaoFluxo } from '../types';
import type {
  AutoSaveStatus,
  BlocoLocal,
  EtapaLocal,
  FluxoArbitralEditavel,
} from '../types/editor';

import { arquivarFluxoArbitral, publicarFluxoArbitral } from './actions';
import {
  fetchCapabilities,
  fetchFluxoArbitral,
  fetchVersoesFluxo,
  salvarFluxoArbitral,
  validarFluxoArbitral,
} from './editorActions';

export interface FluxoEditorState {
  fluxo: FluxoArbitralEditavel | null;
  loading: boolean;
  error: string | null;
  saveStatus: AutoSaveStatus;
  saveError: string | null;
  etapaSelecionadaId: string | null;
  blocoSelecionadoId: string | null;
  validando: boolean;
  /** `nodeTypes` do motor; `null` até carregar (cai no fallback estático). */
  capabilities: string[] | null;
  /** `features` do motor; vazio até carregar, o que esconde o que depende delas. */
  recursosMotor: string[];
  versoes: VersaoFluxo[] | null;
  carregandoVersoes: boolean;
  versoesError: string | null;
  conflitoVersion: number | null;
}

const INITIAL_STATE: FluxoEditorState = {
  fluxo: null,
  loading: false,
  error: null,
  saveStatus: 'idle',
  saveError: null,
  etapaSelecionadaId: null,
  blocoSelecionadoId: null,
  validando: false,
  capabilities: null,
  recursosMotor: [],
  versoes: null,
  carregandoVersoes: false,
  versoesError: null,
  conflitoVersion: null,
};

interface MoverPayload {
  id: string;
  direcao: -1 | 1;
}

interface AtualizarEtapaPayload {
  etapaId: string;
  patch: Partial<Omit<EtapaLocal, 'id' | 'blocos'>>;
}

interface AdicionarBlocoPayload {
  etapaId: string;
  bloco: BlocoLocal;
}

interface AtualizarBlocoPayload {
  etapaId: string;
  blocoId: string;
  patch: Partial<Omit<BlocoLocal, 'id'>>;
}

interface RemoverBlocoPayload {
  etapaId: string;
  blocoId: string;
}

interface MoverBlocoPayload extends MoverPayload {
  etapaId: string;
}

interface MoverEtapaParaIndicePayload {
  id: string;
  destino: number;
}

interface MoverBlocoParaPosicaoPayload {
  blocoId: string;
  deEtapaId: string;
  paraEtapaId: string;
  destinoIndex: number;
}

function reordenar<T extends { ordem: number }>(items: T[]): T[] {
  return items
    .slice()
    .sort((a, b) => a.ordem - b.ordem)
    .map((item, idx) => ({ ...item, ordem: idx }));
}

const slice = createSlice({
  name: 'fluxoEditor',
  initialState: INITIAL_STATE,
  reducers: {
    selecionarEtapa(state, action: PayloadAction<string | null>) {
      state.etapaSelecionadaId = action.payload;
      state.blocoSelecionadoId = null;
    },
    selecionarBloco(state, action: PayloadAction<string | null>) {
      state.blocoSelecionadoId = action.payload;
    },
    setSaveStatus(state, action: PayloadAction<AutoSaveStatus>) {
      state.saveStatus = action.payload;
    },
    renomearFluxo(state, action: PayloadAction<string>) {
      if (state.fluxo) state.fluxo.name = action.payload;
    },
    atualizarDescricaoFluxo(state, action: PayloadAction<string | null>) {
      if (state.fluxo) state.fluxo.description = action.payload;
    },
    adicionarEtapa(state, action: PayloadAction<EtapaLocal>) {
      if (!state.fluxo) return;
      state.fluxo.etapas.push(action.payload);
      state.fluxo.etapas = reordenar(state.fluxo.etapas);
      state.etapaSelecionadaId = action.payload.id;
      state.blocoSelecionadoId = null;
    },
    atualizarEtapa(state, action: PayloadAction<AtualizarEtapaPayload>) {
      if (!state.fluxo) return;
      const { etapaId, patch } = action.payload;
      state.fluxo.etapas = state.fluxo.etapas.map((e) =>
        e.id === etapaId ? { ...e, ...patch } : e,
      );
    },
    removerEtapa(state, action: PayloadAction<string>) {
      if (!state.fluxo) return;
      state.fluxo.etapas = reordenar(state.fluxo.etapas.filter((e) => e.id !== action.payload));
      if (state.etapaSelecionadaId === action.payload) {
        state.etapaSelecionadaId = state.fluxo.etapas[0]?.id ?? null;
        state.blocoSelecionadoId = null;
      }
    },
    moverEtapa(state, action: PayloadAction<MoverPayload>) {
      if (!state.fluxo) return;
      const { id, direcao } = action.payload;
      const etapas = state.fluxo.etapas.slice().sort((a, b) => a.ordem - b.ordem);
      const idx = etapas.findIndex((e) => e.id === id);
      const alvo = idx + direcao;
      if (idx < 0 || alvo < 0 || alvo >= etapas.length) return;
      const tmp = etapas[idx];
      etapas[idx] = { ...etapas[alvo], ordem: idx };
      etapas[alvo] = { ...tmp, ordem: alvo };
      state.fluxo.etapas = etapas;
    },
    adicionarBloco(state, action: PayloadAction<AdicionarBlocoPayload>) {
      if (!state.fluxo) return;
      const { etapaId, bloco } = action.payload;
      state.fluxo.etapas = state.fluxo.etapas.map((e) => {
        if (e.id !== etapaId) return e;
        const blocos = [...e.blocos, bloco];
        return { ...e, blocos: reordenar(blocos) };
      });
      state.blocoSelecionadoId = bloco.id;
    },
    atualizarBloco(state, action: PayloadAction<AtualizarBlocoPayload>) {
      if (!state.fluxo) return;
      const { etapaId, blocoId, patch } = action.payload;
      state.fluxo.etapas = state.fluxo.etapas.map((e) => {
        if (e.id !== etapaId) return e;
        return {
          ...e,
          blocos: e.blocos.map((b) => (b.id === blocoId ? { ...b, ...patch } : b)),
        };
      });
    },
    removerBloco(state, action: PayloadAction<RemoverBlocoPayload>) {
      if (!state.fluxo) return;
      const { etapaId, blocoId } = action.payload;
      state.fluxo.etapas = state.fluxo.etapas.map((e) => {
        if (e.id !== etapaId) return e;
        return { ...e, blocos: reordenar(e.blocos.filter((b) => b.id !== blocoId)) };
      });
      if (state.blocoSelecionadoId === blocoId) state.blocoSelecionadoId = null;
    },
    moverBloco(state, action: PayloadAction<MoverBlocoPayload>) {
      if (!state.fluxo) return;
      const { etapaId, id, direcao } = action.payload;
      state.fluxo.etapas = state.fluxo.etapas.map((e) => {
        if (e.id !== etapaId) return e;
        const blocos = e.blocos.slice().sort((a, b) => a.ordem - b.ordem);
        const idx = blocos.findIndex((b) => b.id === id);
        const alvo = idx + direcao;
        if (idx < 0 || alvo < 0 || alvo >= blocos.length) return e;
        const tmp = blocos[idx];
        blocos[idx] = { ...blocos[alvo], ordem: idx };
        blocos[alvo] = { ...tmp, ordem: alvo };
        return { ...e, blocos };
      });
    },
    moverEtapaParaIndice(state, action: PayloadAction<MoverEtapaParaIndicePayload>) {
      if (!state.fluxo) return;
      const { id, destino } = action.payload;
      const etapas = state.fluxo.etapas.slice().sort((a, b) => a.ordem - b.ordem);
      const idx = etapas.findIndex((e) => e.id === id);
      if (idx < 0) return;
      const [movida] = etapas.splice(idx, 1);
      const alvo = idx < destino ? destino - 1 : destino;
      etapas.splice(Math.min(Math.max(alvo, 0), etapas.length), 0, movida);
      state.fluxo.etapas = etapas.map((e, ordem) => ({ ...e, ordem }));
    },
    moverBlocoParaPosicao(state, action: PayloadAction<MoverBlocoParaPosicaoPayload>) {
      if (!state.fluxo) return;
      const { blocoId, deEtapaId, paraEtapaId, destinoIndex } = action.payload;
      const origem = state.fluxo.etapas.find((e) => e.id === deEtapaId);
      const destinoEtapa = state.fluxo.etapas.find((e) => e.id === paraEtapaId);
      if (!origem || !destinoEtapa) return;
      const blocosOrigem = origem.blocos.slice().sort((a, b) => a.ordem - b.ordem);
      const idx = blocosOrigem.findIndex((b) => b.id === blocoId);
      if (idx < 0) return;
      const [movido] = blocosOrigem.splice(idx, 1);
      const mesmaEtapa = deEtapaId === paraEtapaId;
      const blocosDestino = mesmaEtapa
        ? blocosOrigem
        : destinoEtapa.blocos.slice().sort((a, b) => a.ordem - b.ordem);
      const alvo = mesmaEtapa && idx < destinoIndex ? destinoIndex - 1 : destinoIndex;
      blocosDestino.splice(Math.min(Math.max(alvo, 0), blocosDestino.length), 0, movido);
      state.fluxo.etapas = state.fluxo.etapas.map((e) => {
        if (e.id === paraEtapaId) {
          return { ...e, blocos: blocosDestino.map((b, ordem) => ({ ...b, ordem })) };
        }
        if (e.id === deEtapaId) {
          return { ...e, blocos: blocosOrigem.map((b, ordem) => ({ ...b, ordem })) };
        }
        return e;
      });
      if (state.blocoSelecionadoId === blocoId) {
        state.etapaSelecionadaId = paraEtapaId;
      }
    },
    substituirEtapas(state, action: PayloadAction<EtapaLocal[]>) {
      if (!state.fluxo) return;
      state.fluxo.etapas = reordenar(action.payload);
      state.etapaSelecionadaId = state.fluxo.etapas[0]?.id ?? null;
      state.blocoSelecionadoId = null;
    },
    aplicarVersaoServidor(state, action: PayloadAction<number>) {
      if (state.fluxo) state.fluxo.version = action.payload;
      state.conflitoVersion = null;
      state.saveStatus = 'idle';
    },
    resetarEditor() {
      return INITIAL_STATE;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFluxoArbitral.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFluxoArbitral.fulfilled, (state, action) => {
        state.loading = false;
        state.fluxo = action.payload;
        state.etapaSelecionadaId = action.payload.etapas[0]?.id ?? null;
        state.blocoSelecionadoId = null;
        state.saveStatus = 'salvo';
      })
      .addCase(fetchFluxoArbitral.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? action.error.message ?? 'Erro';
      })
      .addCase(salvarFluxoArbitral.pending, (state) => {
        state.saveStatus = 'salvando';
        state.saveError = null;
      })
      .addCase(salvarFluxoArbitral.fulfilled, (state, action) => {
        state.saveStatus = 'salvo';
        state.conflitoVersion = null;
        if (state.fluxo) {
          state.fluxo.status = action.payload.status;
          state.fluxo.version = action.payload.version;
        }
      })
      .addCase(salvarFluxoArbitral.rejected, (state, action) => {
        if (action.payload?.tipo === 'conflito') {
          state.saveStatus = 'conflito';
          state.conflitoVersion = action.payload.versionAtual ?? null;
          state.saveError = action.payload.message;
        } else {
          state.saveStatus = 'erro';
          state.saveError = action.payload?.message ?? action.error.message ?? 'Erro ao salvar';
        }
      })
      .addCase(validarFluxoArbitral.pending, (state) => {
        state.validando = true;
      })
      .addCase(validarFluxoArbitral.fulfilled, (state) => {
        state.validando = false;
      })
      .addCase(validarFluxoArbitral.rejected, (state) => {
        state.validando = false;
      })
      .addCase(fetchCapabilities.fulfilled, (state, action) => {
        state.capabilities = action.payload.nodeTypes;
        state.recursosMotor = action.payload.features;
      })
      .addCase(fetchVersoesFluxo.pending, (state) => {
        state.carregandoVersoes = true;
        state.versoesError = null;
      })
      .addCase(fetchVersoesFluxo.fulfilled, (state, action) => {
        state.carregandoVersoes = false;
        state.versoes = action.payload;
        state.versoesError = null;
      })
      .addCase(fetchVersoesFluxo.rejected, (state, action) => {
        state.carregandoVersoes = false;
        state.versoesError = action.payload ?? 'Erro ao carregar versões';
      })
      .addCase(publicarFluxoArbitral.fulfilled, (state, action) => {
        if (state.fluxo && state.fluxo.id === action.payload.id) {
          state.fluxo.status = action.payload.status;
        }
      })
      .addCase(arquivarFluxoArbitral.fulfilled, (state, action) => {
        if (state.fluxo && state.fluxo.id === action.payload.id) {
          state.fluxo.status = action.payload.status;
        }
      });
  },
});

export const {
  aplicarVersaoServidor,
  adicionarBloco,
  adicionarEtapa,
  atualizarBloco,
  atualizarDescricaoFluxo,
  atualizarEtapa,
  moverBloco,
  moverBlocoParaPosicao,
  moverEtapa,
  moverEtapaParaIndice,
  removerBloco,
  removerEtapa,
  renomearFluxo,
  resetarEditor,
  selecionarBloco,
  selecionarEtapa,
  setSaveStatus,
  substituirEtapas,
} = slice.actions;

export const fluxoEditorReducer = slice.reducer;
